// Datos de Outbound Loads: la lista de cargas de salida y, bajo demanda, el
// detalle de líneas de una carga.
//
// Sin paginar ni acotar por fecha desde aquí: la pantalla agrupa por
// proximidad (atrasadas / hoy / esta semana / …) y para eso necesita el
// conjunto entero que ya ha filtrado el backend (vivas, ≤30 días).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cargasSalidaApi } from '@/services/api'
import type { AccionLinea, EstadoManualBody } from '@/types'

export function useCargasSalida(params: { incluirExpedidas?: boolean; almacen?: string }) {
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['cargas-salida', params.incluirExpedidas ?? false, params.almacen ?? ''],
    queryFn: () => cargasSalidaApi.list(params),
    staleTime: 60_000,
  })

  return {
    cargas: data?.data ?? [],
    updatedAt: data?.updatedAt,
    isLoading,
    isError,
    isFetching,
    refetch,
  }
}

/** Líneas de UNA carga, para el desplegable. `carga` a `null` no consulta nada:
 *  es lo que hay antes de abrir la fila y al cerrarla, y no tiene sentido pedir
 *  el detalle de una carga que no se está mirando. */
export function useCargaSalidaLineas(carga: string | null) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cargas-salida', carga, 'lineas'],
    queryFn: () => cargasSalidaApi.getLineas(carga as string),
    enabled: !!carga,
    staleTime: 60_000,
  })

  return {
    lineas: data?.data ?? [],
    isLoading,
    isError,
  }
}

/** Diccionario de descripciones de artículo. Casi no cambia: caché larga. Si
 *  falla o tarda, el filtro sigue funcionando, sólo que sin descripciones. */
export function useArticulosCatalogo() {
  const { data } = useQuery({
    queryKey: ['cargas-salida', 'articulos'],
    queryFn: () => cargasSalidaApi.getArticulos(),
    staleTime: 30 * 60_000,
  })
  return data?.data
}

// ── Acciones por línea, estado manual, aviso, back orders y facturas ─────────
//
// Todo lo que ESCRIBE invalida el prefijo `['cargas-salida']`: la lista (los
// contadores de BO, el estado y los avisos viven en ella) y las líneas abiertas.
// Es más ancho de lo estricto a propósito: una carga cambia de estado al marcar
// transporte y de contador de BO al decidir una línea, y acertar con la clave
// exacta de cada vista es justo lo que se desincroniza con el tiempo.
//
// La ÚNICA excepción son las facturas y sus adjuntos: ninguna de estas
// escrituras las cambia, y refrescar un adjunto vuelve a lanzar el flujo de
// Power Automate que trae el PDF — segundos y una ejecución del flujo por nada.
function invalidarCargas(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({
    queryKey: ['cargas-salida'],
    predicate: q => !q.queryKey.includes('facturas'),
  })
}

/** Decide qué hacer con una línea (parcial / esperar / eliminar / quitar). */
export function useAccionLinea(carga: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { transid: string; accion: AccionLinea | null }) =>
      cargasSalidaApi.setAccionLinea(carga, v.transid, v.accion),
    onSuccess: () => invalidarCargas(qc),
  })
}

/** Back orders vivos y cerrados. La pestaña filtra en cliente: son pocos. */
export function useBackOrders() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['cargas-salida', 'back-orders'],
    queryFn: () => cargasSalidaApi.getBackOrders(),
    staleTime: 60_000,
  })
  return { backOrders: data?.data ?? [], isLoading, isError, refetch, isFetching }
}

export function useCancelarBackOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { id: string; motivo: string }) =>
      cargasSalidaApi.cancelarBackOrder(v.id, v.motivo),
    onSuccess: () => invalidarCargas(qc),
  })
}

/** Marca manual de transporte (o su retirada con `estado: null`). */
export function useEstadoManual(carga: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EstadoManualBody) => cargasSalidaApi.setEstadoManual(carga, body),
    onSuccess: () => invalidarCargas(qc),
  })
}

export function useAvisoCliente(carga: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => cargasSalidaApi.avisarCliente(carga),
    onSuccess: () => invalidarCargas(qc),
  })
}

/** Facturas de los pedidos de la carga. `activo` evita pedirlas con la ficha cerrada. */
export function useFacturasCarga(carga: string | null) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cargas-salida', carga, 'facturas'],
    queryFn: () => cargasSalidaApi.getFacturas(carga as string),
    enabled: !!carga,
    staleTime: 60_000,
  })
  return { facturas: data?.data ?? [], isLoading, isError }
}
